// Project Analytics — PRODUCT-CONCENTRIC production analysis (no operator / machine).
// Source: MongoDB `ProductionMetric` (one doc per completed token) via
// GET /api/v1/production-metrics/product-analytics → KPIs + per-product breakdown +
// meters-over-time series + by-phase / by-color splits. Also a scan/JOB/TOKEN lookup
// that opens project-details (fallback when a label QR can't be scanned).
//
// The product name in the breakdown is also a way into the project that made it
// — see ProductCell for why that is a list rather than a single link.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import { Card, Row, Col, Form, Button, Spinner, Badge, Dropdown } from 'react-bootstrap';
import ReactEChartsCore from '../../components/charts/EChart';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { BarChart, PieChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../providers/AppProvider';
import AdvanceTable from '../../components/base/AdvanceTable';
import AdvanceTableFooter from '../../components/base/AdvanceTableFooter';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import FilterTab from '../../components/common/FilterTab';
import SearchBox from '../../components/common/SearchBox';
import FilterButtonGroup from '../../components/common/FilterButtonGroup';
import { API } from '../../config/api';

echarts.use([TooltipComponent, GridComponent, LegendComponent, BarChart, PieChart, CanvasRenderer]);

const PM_API = `${API.BACKEND}/api/v1/production-metrics`;
const REDIS_API = `${API.REDIS}/api/redis`;

const PHASES = ['conductor_isolation', 'rope', 'interior_sheat', 'armouring', 'exterior_sheat', 'coiling', 'rewinding'];
const fmtInt = n => Math.round(Number(n) || 0).toLocaleString('en-US');
const fmt1 = n => (Number.isFinite(Number(n)) ? Number(n).toFixed(1) : '—');
const humanize = s => String(s || '').split('_').filter(Boolean).map(w => w[0].toUpperCase() + w.slice(1)).join(' ');
const familyOf = label => String(label || '').trim().split(/\s+/)[0] || '—';

const colorHex = c => ({
  'GREEN-YELLOW': '#9acd32', BLUE: '#2f7cff', BROWN: '#8b5a2b', BLACK: '#111827',
  GREY: '#7a7a7a', GRAY: '#7a7a7a', WHITE: '#d0d5dd', RED: '#d9534f', YELLOW: '#f0c400',
  GREEN: '#2e8b57', ORANGE: '#f39c12'
}[String(c || '').toUpperCase()] || '#8892a0');

const palette = isDark =>
  isDark
    ? ['#60A5FA', '#22D3EE', '#34D399', '#A78BFA', '#FBBF24', '#FB7185', '#F472B6', '#F97316', '#93C5FD', '#5EEAD4']
    : ['#2563EB', '#0891B2', '#059669', '#7C3AED', '#D97706', '#E11D48', '#BE185D', '#EA580C', '#1D4ED8', '#0F766E'];

const axisColor = isDark => (isDark ? '#E5E7EB' : '#0F172A');
const splitColor = isDark => (isDark ? '#334155' : '#E2E8F0');

// ── charts (theme-aware) ──
const metersTimeOption = (series, bucket, isDark) => {
  if (!series?.length) return null;
  const buckets = [...new Set(series.map(s => s.bucket))].sort();
  const prods = [...new Set(series.map(s => s.productLabel))];
  const byKey = {};
  series.forEach(s => { byKey[`${s.productLabel}|${s.bucket}`] = s.meters; });
  return {
    color: palette(isDark),
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { type: 'scroll', top: 0, textStyle: { color: axisColor(isDark), fontWeight: 600 } },
    grid: { left: 8, right: 12, bottom: 8, top: 44, containLabel: true },
    xAxis: {
      type: 'category', data: buckets,
      axisLabel: { color: axisColor(isDark), rotate: buckets.length > 10 ? 45 : 0 },
      axisLine: { lineStyle: { color: splitColor(isDark) } }, axisTick: { show: false }
    },
    yAxis: {
      type: 'value', name: 'meters', nameTextStyle: { color: axisColor(isDark) },
      axisLabel: { color: axisColor(isDark) }, splitLine: { lineStyle: { color: splitColor(isDark) } }
    },
    series: prods.map((p, i) => ({
      name: p, type: 'bar', stack: 'm', barMaxWidth: 26, emphasis: { focus: 'series' },
      itemStyle: { borderRadius: i === prods.length - 1 ? [4, 4, 0, 0] : 0 },
      data: buckets.map(b => Math.round(byKey[`${p}|${b}`] || 0))
    }))
  };
};

const topProductsOption = (perProduct, isDark) => {
  const rows = [...(perProduct || [])].sort((a, b) => b.meters - a.meters).slice(0, 12).reverse();
  if (!rows.length) return null;
  return {
    color: [palette(isDark)[0]],
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, valueFormatter: v => `${fmtInt(v)} m` },
    grid: { left: 8, right: 24, bottom: 8, top: 12, containLabel: true },
    xAxis: { type: 'value', axisLabel: { color: axisColor(isDark) }, splitLine: { lineStyle: { color: splitColor(isDark) } } },
    yAxis: {
      type: 'category', data: rows.map(r => r.productLabel),
      axisLabel: { color: axisColor(isDark) }, axisLine: { lineStyle: { color: splitColor(isDark) } }, axisTick: { show: false }
    },
    series: [{
      type: 'bar', barMaxWidth: 18, itemStyle: { borderRadius: [0, 4, 4, 0] },
      data: rows.map(r => Math.round(r.meters)),
      label: { show: true, position: 'right', color: axisColor(isDark), formatter: p => `${fmtInt(p.value)} m` }
    }]
  };
};

const byPhaseOption = (byPhase, isDark) => {
  const rows = (byPhase || []).filter(p => p.meters > 0);
  if (!rows.length) return null;
  return {
    color: palette(isDark),
    tooltip: { trigger: 'item', valueFormatter: v => `${fmtInt(v)} m` },
    legend: { type: 'scroll', bottom: 0, textStyle: { color: axisColor(isDark) } },
    series: [{
      type: 'pie', radius: ['42%', '68%'], center: ['50%', '46%'], avoidLabelOverlap: true,
      itemStyle: { borderColor: isDark ? '#141824' : '#fff', borderWidth: 2 },
      label: { color: axisColor(isDark), formatter: '{b}\n{d}%' },
      data: rows.map(p => ({ name: humanize(p.phase), value: Math.round(p.meters) }))
    }]
  };
};

const byColorOption = (byColor, isDark) => {
  const rows = (byColor || []).filter(c => c.meters > 0).slice(0, 12);
  if (!rows.length) return null;
  return {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, valueFormatter: v => `${fmtInt(v)} m` },
    grid: { left: 8, right: 12, bottom: 8, top: 12, containLabel: true },
    xAxis: {
      type: 'category', data: rows.map(c => c.color),
      axisLabel: { color: axisColor(isDark), rotate: rows.length > 6 ? 30 : 0 },
      axisLine: { lineStyle: { color: splitColor(isDark) } }, axisTick: { show: false }
    },
    yAxis: { type: 'value', axisLabel: { color: axisColor(isDark) }, splitLine: { lineStyle: { color: splitColor(isDark) } } },
    series: [{
      type: 'bar', barMaxWidth: 40, itemStyle: { borderRadius: [4, 4, 0, 0] },
      data: rows.map(c => ({ value: Math.round(c.meters), itemStyle: { color: colorHex(c.color) } }))
    }]
  };
};

// ── scan / JOB / TOKEN lookup → opens project-details ──
function ProductLookup() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  /**
   * One question, asked of the backend.
   *
   * This used to pull `/physical-tokens` and search it client-side. That
   * endpoint hides tokens the warehouse already holds — deliberately, so
   * Project Bobin State does not list cable that is already on a rack — which
   * made a token unfindable at exactly the moment somebody scans its label:
   * once it had been received. It also never looked in the archive, so a
   * finished job was gone the moment it was swept out of Redis.
   *
   * `/jobs/resolve` looks in both, and matches a job id, a job code, a token id
   * or the QR payload printed on the label.
   */
  const go = async () => {
    const raw = q.trim();
    if (!raw) return;
    setBusy(true); setMsg(null);
    try {
      const { data } = await axios.get(`${REDIS_API}/jobs/resolve`, {
        params: { q: raw }
      });
      if (data?.jobId) {
        navigate(`/apps/project-management/project-details/${data.jobId}`);
        return;
      }
      setMsg('No job/product found for this code.');
    } catch (e) {
      setMsg(
        e?.response?.status === 404
          ? 'No job or product found for this code.'
          : 'Lookup error: ' + (e.message || 'unknown')
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-3 border-primary">
      <Card.Body className="py-2">
        <Row className="g-2 align-items-center">
          <Col xs={12} md="auto">
            <div className="fw-bold">Product / job lookup</div>
            <div className="fs-10 text-600">Scan QR or paste JOB ID / TOKEN ID</div>
          </Col>
          <Col>
            <Form.Control size="sm" value={q} onChange={e => setQ(e.target.value)}
              placeholder="JOB ID, TOKEN ID or scanned URL…"
              onKeyDown={e => { if (e.key === 'Enter') go(); }} />
          </Col>
          <Col xs="auto"><Button size="sm" onClick={go} disabled={busy}>{busy ? 'Searching…' : 'Open'}</Button></Col>
        </Row>
        {msg && <div className="text-danger fs-10 mt-1">{msg}</div>}
      </Card.Body>
    </Card>
  );
}

function KpiTile({ label, value, sub }) {
  return (
    <Card className="h-100">
      <Card.Body className="py-2">
        <div className="fs-10 text-600 text-uppercase fw-bold">{label}</div>
        <div className="fs-3 fw-bold">{value}</div>
        {sub && <div className="fs-10 text-500">{sub}</div>}
      </Card.Body>
    </Card>
  );
}

const numMeta = width => ({
  cellProps: { className: 'text-end white-space-nowrap' },
  headerProps: { className: 'text-end', style: { width } }
});

/**
 * The product name, as a way into the project that made it.
 *
 * A label is a product TYPE, not a project. Over a month MYYM 3x2.5 was made by
 * six different jobs, and 9 of 27 labels span more than one — so a single click
 * cannot pick "the" job without inventing an answer. One job opens directly;
 * several offer the list, newest first, with enough on each row (drums, metres,
 * when) to tell them apart.
 */
const ProductCell = ({ row, onOpen }) => {
  const jobs = Array.isArray(row.jobs) ? row.jobs : [];
  const dot = (
    <span style={{ width: 10, height: 10, borderRadius: '50%', display: 'inline-block', flex: '0 0 auto',
      background: colorHex(row.colors?.[0]), border: '1px solid rgba(0,0,0,0.2)' }} />
  );

  if (!jobs.length) {
    // Metrics written before jobId was recorded. Shown plainly rather than as a
    // link that would go nowhere.
    return (
      <span className="fw-bold d-inline-flex align-items-center gap-2" title="No job recorded for this production">
        {dot}{row.productLabel}
      </span>
    );
  }

  if (jobs.length === 1) {
    return (
      <Button variant="link" size="sm"
        className="fw-bold p-0 text-start text-decoration-none d-inline-flex align-items-center gap-2"
        onClick={() => onOpen(jobs[0].jobId)}
        title={`Open ${jobs[0].jobCode || jobs[0].jobId}`}>
        {dot}<span className="text-decoration-underline">{row.productLabel}</span>
      </Button>
    );
  }

  return (
    <Dropdown>
      <Dropdown.Toggle variant="link" size="sm"
        className="fw-bold p-0 text-start text-decoration-none d-inline-flex align-items-center gap-2">
        {dot}
        <span className="text-decoration-underline">{row.productLabel}</span>
        <Badge bg="secondary" className="ms-1">{jobs.length} projects</Badge>
      </Dropdown.Toggle>
      <Dropdown.Menu style={{ maxHeight: 320, overflowY: 'auto' }}>
        <Dropdown.Header className="fs-10">
          {row.productLabel} — made by {jobs.length} projects
        </Dropdown.Header>
        {jobs.map(j => (
          <Dropdown.Item key={j.jobId} onClick={() => onOpen(j.jobId)} className="fs-9">
            <div className="fw-semibold">{j.jobCode || j.jobId}</div>
            <div className="fs-10 text-700">
              {fmtInt(j.tokens)} drums · {fmtInt(j.meters)} m
              {j.lastAt ? ` · ${dayjs(Number(j.lastAt)).format('DD.MM.YYYY')}` : ''}
            </div>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

// AdvanceTable columns for the per-product breakdown. A function, not a const:
// the product cell needs the router, and a column array built at module scope
// cannot reach it.
const buildProductColumns = onOpen => [
  {
    accessorKey: 'productLabel', header: 'Product',
    cell: ({ row: { original } }) => <ProductCell row={original} onOpen={onOpen} />,
    meta: { headerProps: { style: { width: '18%' } } }
  },
  { accessorKey: 'meters', header: 'Meters', cell: ({ row: { original } }) => `${fmtInt(original.meters)} m`, meta: numMeta('10%') },
  { accessorKey: 'tokens', header: 'Drums', cell: ({ row: { original } }) => fmtInt(original.tokens), meta: numMeta('8%') },
  { accessorKey: 'avgLen', header: 'Avg length', cell: ({ row: { original } }) => `${fmtInt(original.avgLen)} m`, meta: numMeta('10%') },
  { accessorKey: 'avgSpeed', header: 'Avg speed', cell: ({ row: { original } }) => `${fmt1(original.avgSpeed)} m/min`, meta: numMeta('10%') },
  {
    accessorKey: 'phasesStr', header: 'Phases',
    cell: ({ row: { original } }) => (original.phases || []).map(p => <Badge key={p} bg="info" className="me-1">{humanize(p)}</Badge>),
    meta: { headerProps: { style: { width: '18%' } } }
  },
  {
    accessorKey: 'colorsStr', header: 'Colors',
    cell: ({ row: { original } }) => (original.colors || []).map(c => (
      <Badge key={c} bg="" className="me-1" style={{ background: colorHex(c), color: '#fff' }}>{c}</Badge>
    )),
    meta: { headerProps: { style: { width: '14%' } } }
  },
  {
    accessorKey: 'lastAt', header: 'Last produced',
    cell: ({ row: { original } }) => (original.lastAt ? dayjs(Number(original.lastAt)).format('DD.MM.YYYY') : '—'),
    meta: { headerProps: { style: { width: '12%' } } }
  }
];

function ProductBreakdownTable({ rows, familyTabs, familyFilter, setFamilyFilter, filterMenus, onSearch }) {
  const navigate = useNavigate();
  const openProject = useCallback(
    jobId => navigate(`/apps/project-management/project-details/${jobId}`),
    [navigate]
  );
  // Memoised on the handler alone: rebuilding the column array on every render
  // gives the table new column identities each time, which resets sorting and
  // the page the user is on.
  const columns = useMemo(() => buildProductColumns(openProject), [openProject]);

  const table = useAdvanceTable({
    data: rows, columns, pageSize: 15,
    pagination: true, sortable: true, selection: false, autoResetPageIndex: false
  });
  return (
    <>
      <FilterTab tabItems={familyTabs} activeKey={familyFilter} onSelect={setFamilyFilter} className="mb-2" />
      <AdvanceTableProvider {...table}>
        <div className="d-flex flex-wrap gap-3 mb-3">
          <SearchBox placeholder="Search product…" onChange={onSearch} />
          <div className="scrollbar overflow-hidden-y"><FilterButtonGroup menus={filterMenus} /></div>
        </div>
        <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
        <AdvanceTableFooter pagination />
      </AdvanceTableProvider>
    </>
  );
}

const ProjectAnalytics = () => {
  const { config: { theme } } = useAppContext();
  const isDark = theme === 'dark';

  const [from, setFrom] = useState(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
  const [to, setTo] = useState(dayjs().format('YYYY-MM-DD'));
  const [bucket, setBucket] = useState('day');
  const [phase, setPhase] = useState('');
  const [selected, setSelected] = useState([]); // product filter → backend
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // table-level quick filters (client side)
  const [familyFilter, setFamilyFilter] = useState('all');
  const [colorFilter, setColorFilter] = useState('all');
  const [minMeters, setMinMeters] = useState(0);
  const [search, setSearch] = useState('');

  const load = useCallback(async (products = selected) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({
        dateFrom: String(dayjs(from).startOf('day').valueOf()),
        dateTo: String(dayjs(to).endOf('day').valueOf()),
        bucket
      });
      if (products.length) params.set('products', products.join(','));
      if (phase) params.set('phase', phase);
      const res = await axios.get(`${PM_API}/product-analytics?${params.toString()}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [from, to, bucket, phase, selected]);

  useEffect(() => {
    load([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleProduct = p => setSelected(prev => {
    const next = prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p];
    load(next);
    return next;
  });

  const allProducts = useMemo(() => data?.allProducts || [], [data]);
  const perProduct = useMemo(() => data?.perProduct || [], [data]);
  const kpi = data?.kpi || { meters: 0, tokens: 0, timeSec: 0, avgSpeed: 0, productCount: 0 };

  const familyTabs = useMemo(() => {
    const counts = {};
    perProduct.forEach(p => { const f = familyOf(p.productLabel); counts[f] = (counts[f] || 0) + 1; });
    const tabs = [{ label: 'All', value: 'all', count: perProduct.length }];
    Object.keys(counts).sort().forEach(f => tabs.push({ label: f, value: f, count: counts[f] }));
    return tabs;
  }, [perProduct]);

  const filterMenus = useMemo(() => {
    const colors = [...new Set(perProduct.flatMap(p => p.colors || []))].filter(Boolean).sort();
    return [
      {
        label: 'Color',
        items: [{ label: 'All', onClick: () => setColorFilter('all') },
          ...colors.map(c => ({ label: c, onClick: () => setColorFilter(c) }))]
      },
      {
        label: 'Min meters',
        items: [
          { label: 'All', onClick: () => setMinMeters(0) },
          { label: '≥ 100 m', onClick: () => setMinMeters(100) },
          { label: '≥ 1,000 m', onClick: () => setMinMeters(1000) },
          { label: '≥ 5,000 m', onClick: () => setMinMeters(5000) }
        ]
      }
    ];
  }, [perProduct]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return perProduct.filter(p =>
      (familyFilter === 'all' || familyOf(p.productLabel) === familyFilter) &&
      (colorFilter === 'all' || (p.colors || []).includes(colorFilter)) &&
      (Number(p.meters) >= minMeters) &&
      (!q || String(p.productLabel).toLowerCase().includes(q))
    );
  }, [perProduct, familyFilter, colorFilter, minMeters, search]);

  const bucketLabel = bucket === 'day' ? 'day' : bucket === 'week' ? 'week' : 'month';
  const timeOpt = useMemo(() => metersTimeOption(data?.series, bucket, isDark), [data, bucket, isDark]);
  const topOpt = useMemo(() => topProductsOption(perProduct, isDark), [perProduct, isDark]);
  const phaseOpt = useMemo(() => byPhaseOption(data?.byPhase, isDark), [data, isDark]);
  const colorOpt = useMemo(() => byColorOption(data?.byColor, isDark), [data, isDark]);

  return (
    <div className="p-3">
      <h2 className="mb-1">Project Analytics</h2>
      <p className="text-700">Product-concentric production analysis — meters produced over time, per-product breakdown. (Source: ProductionMetric / completed token.)</p>

      <ProductLookup />

      {/* controls */}
      <Card className="mb-3">
        <Card.Body className="py-2">
          <Row className="g-2 align-items-end">
            <Col xs={6} md={2}>
              <Form.Label className="fs-10 mb-0">From</Form.Label>
              <Form.Control size="sm" type="date" value={from} onChange={e => setFrom(e.target.value)} />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="fs-10 mb-0">To</Form.Label>
              <Form.Control size="sm" type="date" value={to} onChange={e => setTo(e.target.value)} />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="fs-10 mb-0">Interval</Form.Label>
              <Form.Select size="sm" value={bucket} onChange={e => setBucket(e.target.value)}>
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </Form.Select>
            </Col>
            <Col xs={6} md={3}>
              <Form.Label className="fs-10 mb-0">Phase (optional)</Form.Label>
              <Form.Select size="sm" value={phase} onChange={e => setPhase(e.target.value)}>
                <option value="">All phases</option>
                {PHASES.map(p => <option key={p} value={p}>{humanize(p)}</option>)}
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Button size="sm" onClick={() => load()} disabled={loading}>
                {loading ? <Spinner size="sm" animation="border" /> : 'Load'}
              </Button>
            </Col>
          </Row>
          {error && <div className="text-danger fs-10 mt-2">Error: {error}</div>}
        </Card.Body>
      </Card>

      {/* product selector (drives the whole analysis) */}
      <Card className="mb-3">
        <Card.Header className="py-2 d-flex justify-content-between align-items-center">
          <span className="fw-bold">Products {selected.length ? `(${selected.length} selected)` : '(all)'}</span>
          {selected.length > 0 && <Button size="sm" variant="outline-secondary" onClick={() => { setSelected([]); load([]); }}>All</Button>}
        </Card.Header>
        <Card.Body className="py-2" style={{ maxHeight: 150, overflowY: 'auto' }}>
          {allProducts.length === 0 ? (
            <span className="text-500 fs-10">No products in range.</span>
          ) : (
            <Row className="g-1">
              {allProducts.map(p => (
                <Col xs={6} md={3} key={p}>
                  <Form.Check type="checkbox" label={p} checked={selected.includes(p)}
                    onChange={() => toggleProduct(p)} className="fs-10" />
                </Col>
              ))}
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* KPIs */}
      <Row className="g-2 mb-3">
        <Col xs={6} md={3}><KpiTile label="Total meters" value={`${fmtInt(kpi.meters)} m`} sub={selected.length ? `${selected.length} products` : 'all products'} /></Col>
        <Col xs={6} md={3}><KpiTile label="Drums (tokens)" value={fmtInt(kpi.tokens)} /></Col>
        <Col xs={6} md={3}><KpiTile label="Distinct products" value={fmtInt(kpi.productCount)} /></Col>
        <Col xs={6} md={3}><KpiTile label="Production hours" value={fmt1(kpi.timeSec / 3600)} sub={`avg speed ${fmt1(kpi.avgSpeed)} m/min`} /></Col>
      </Row>

      {/* main time series */}
      <Card className="mb-3">
        <Card.Header className="py-2 fw-bold">Meters produced per {bucketLabel}</Card.Header>
        <Card.Body>
          {timeOpt ? (
            <ReactEChartsCore echarts={echarts} option={timeOpt} notMerge style={{ height: 380 }} />
          ) : <div className="text-500 text-center py-5">{loading ? 'Loading…' : 'No data in range.'}</div>}
        </Card.Body>
      </Card>

      {/* secondary charts */}
      <Row className="g-3 mb-3">
        <Col md={7}>
          <Card className="h-100">
            <Card.Header className="py-2 fw-bold">Top products by meters</Card.Header>
            <Card.Body>
              {topOpt ? <ReactEChartsCore echarts={echarts} option={topOpt} notMerge style={{ height: 320 }} />
                : <div className="text-500 text-center py-5">No data.</div>}
            </Card.Body>
          </Card>
        </Col>
        <Col md={5}>
          <Card className="h-100">
            <Card.Header className="py-2 fw-bold">Meters by phase</Card.Header>
            <Card.Body>
              {phaseOpt ? <ReactEChartsCore echarts={echarts} option={phaseOpt} notMerge style={{ height: 320 }} />
                : <div className="text-500 text-center py-5">No data.</div>}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Header className="py-2 fw-bold">Meters by color</Card.Header>
        <Card.Body>
          {colorOpt ? <ReactEChartsCore echarts={echarts} option={colorOpt} notMerge style={{ height: 260 }} />
            : <div className="text-500 text-center py-4">No data.</div>}
        </Card.Body>
      </Card>

      {/* per-product breakdown (AdvanceTable) */}
      <Card>
        <Card.Header className="py-2 fw-bold">Per-product breakdown</Card.Header>
        <Card.Body>
          <ProductBreakdownTable
            rows={rows}
            familyTabs={familyTabs}
            familyFilter={familyFilter}
            setFamilyFilter={setFamilyFilter}
            filterMenus={filterMenus}
            onSearch={e => setSearch(e.target.value || '')}
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export default ProjectAnalytics;
